// iter91 — Minimal SPA SEO hook.
// Sets <title>, meta description, OG/Twitter tags, canonical URL, and an
// optional page-scoped JSON-LD block (e.g. FAQPage). Cleans up on unmount
// so the next route doesn't inherit stale tags. No external deps.
//
// Usage in a page component:
//   usePageSEO({
//     title: 'Become a Partner — MyLeaf Legacy',
//     description: 'Earn recurring revenue when you bring families to MyLeaf.',
//     path: '/become-a-partner',
//     ogImage: '/og-image.jpg',           // optional, defaults to /og-image.jpg
//     jsonLd: { '@context': 'https://schema.org', '@type': 'FAQPage', ... },
//   });

import { useEffect } from 'react';

const SITE_URL = 'https://myleaflegacy.com';
const DEFAULT_TITLE = 'MyLeaf Legacy · Forever Here';
const DEFAULT_DESCRIPTION =
  'MyLeaf Legacy — keep their story Forever Here. Premium digital memorials, QR plaques, olive family trees, and a private Family Vault.';
const DEFAULT_OG = '/og-image.jpg';

const SEO_TAG_ATTR = 'data-seo-managed';

function setOrCreateMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  const created = !el;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(SEO_TAG_ATTR, '1');
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  return { el, created };
}

function setOrCreateLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute(SEO_TAG_ATTR, '1');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return el;
}

export function usePageSEO({ title, description, path, ogImage, jsonLd, noindex } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    const finalTitle = title || DEFAULT_TITLE;
    const finalDescription = description || DEFAULT_DESCRIPTION;
    const finalOg = ogImage || DEFAULT_OG;
    const finalUrl = path ? `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}` : SITE_URL + '/';

    document.title = finalTitle;

    setOrCreateMeta('meta[name="description"]', { name: 'description', content: finalDescription });
    setOrCreateMeta('meta[property="og:title"]', { property: 'og:title', content: finalTitle });
    setOrCreateMeta('meta[property="og:description"]', { property: 'og:description', content: finalDescription });
    setOrCreateMeta('meta[property="og:url"]', { property: 'og:url', content: finalUrl });
    setOrCreateMeta('meta[property="og:image"]', { property: 'og:image', content: finalOg });
    setOrCreateMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: finalTitle });
    setOrCreateMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: finalDescription });
    setOrCreateMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: finalOg });
    setOrCreateLink('canonical', finalUrl);

    if (noindex) {
      setOrCreateMeta('meta[name="robots"]', { name: 'robots', content: 'noindex, nofollow' });
    } else {
      setOrCreateMeta('meta[name="robots"]', { name: 'robots', content: 'index, follow, max-image-preview:large' });
    }

    // Page-scoped JSON-LD block.
    let ldEl = null;
    if (jsonLd) {
      ldEl = document.createElement('script');
      ldEl.setAttribute('type', 'application/ld+json');
      ldEl.setAttribute('data-page-jsonld', '1');
      ldEl.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(ldEl);
    }

    return () => {
      document.title = prevTitle;
      // Remove page-scoped JSON-LD on unmount so the next route doesn't
      // inherit stale schema.
      if (ldEl && ldEl.parentNode) ldEl.parentNode.removeChild(ldEl);
    };
  }, [title, description, path, ogImage, jsonLd, noindex]);
}

export default usePageSEO;
